import { useFetchData } from "../../../hooks/useFetch";
import { BaseResponse } from "../../../models/response/base-response";
import { ApiConfig, API_ORDER_EMPLOYEES_ROUTER } from "../../../services/api-config";

export interface UpdateEmployeeWeightParams {
  assigned_weight: number;
  wage_rate?: number;
}

export interface OrderEmployeeAssignment {
  id: number;
  order_id: number;
  employee_id: number;
  role_type: number;
  assigned_weight: number;
  wage_rate: number;
  calculated_wage: number;
  status: number;
  reject_reason?: string;
  note?: string;
  employee?: {
    id: number;
    name: string;
    phone?: string;
  };
}

export type OrderEmployeeListResponse = OrderEmployeeAssignment[];

// Custom hooks for order employee operations
// These must be called inside React components

export function useUpdateEmployeeWeight() {
  const { request, baseResponse, loading } = useFetchData.Post<BaseResponse<OrderEmployeeAssignment>>({
    projectId: ApiConfig.PROJECT_ID.ORDER_SERVICE,
  });

  return {
    fetch: (id: number, params: UpdateEmployeeWeightParams) =>
      request({
        endPoint: API_ORDER_EMPLOYEES_ROUTER.API_UPDATE_WEIGHT(id),
        data: params,
      }),
    response: baseResponse,
    loading,
  };
}

export function useGetEmployeesByOrder() {
  const { request, baseResponse, loading, error } = useFetchData.Get<OrderEmployeeListResponse>({
    projectId: ApiConfig.PROJECT_ID.ORDER_SERVICE,
  });

  return {
    fetch: (orderId: number) =>
      request({
        endPoint: API_ORDER_EMPLOYEES_ROUTER.API_BY_ORDER(orderId),
      }),
    response: baseResponse,
    loading,
    error,
  };
}

export function useListOrderEmployees() {
  const { request, baseResponse, loading, error } = useFetchData.Get<OrderEmployeeListResponse>({
    projectId: ApiConfig.PROJECT_ID.ORDER_SERVICE,
  });

  return {
    fetch: (params: { order_id?: number; employee_id?: number; role_type?: number } = {}) =>
      request({
        endPoint: API_ORDER_EMPLOYEES_ROUTER.API_LIST,
        data: params,
      }),
    response: baseResponse,
    loading,
    error,
  };
}

export function useDistributeWeight() {
  const { request, baseResponse, loading } = useFetchData.Post<BaseResponse<null>>({
    projectId: ApiConfig.PROJECT_ID.ORDER_SERVICE,
  });

  return {
    fetch: (orderId: number, roleType: number) =>
      request({
        endPoint: API_ORDER_EMPLOYEES_ROUTER.API_DISTRIBUTE_WEIGHT(orderId),
        data: { role_type: roleType },
      }),
    response: baseResponse,
    loading,
  };
}

// Service object for backwards compatibility
const OrderEmployeeService = {
  updateWeight: useUpdateEmployeeWeight,
  getByOrder: useGetEmployeesByOrder,
  list: useListOrderEmployees,
  distributeWeight: useDistributeWeight,
};

export default OrderEmployeeService;
